import { zodResolver } from '@hookform/resolvers/zod'
import { Info, Loader2, ShieldCheck } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { ConfirmModal } from '@/components/ui/modal'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { isApiError } from '@/lib/api/errors'
import { parseUserRoleReassignmentWarning, useAccessRoles, useSaveRole } from '../api'
import { ROLE_SCOPES, type Role, type RoleFormValues, type RoleInput, type UserRoleReassignmentWarning } from '../types'
import { toastError } from '../ui'
import { useAccessLookups } from '../useLookups'
import { FormDrawer } from './FormDrawer'
import { PermissionPicker } from './PermissionPicker'
import { RoleInfoFields } from './RoleInfoFields'
import { UserPicker } from './UserPicker'

type Props = { open: boolean; onOpenChange: (v: boolean) => void; role?: Role | null; onSaved?: (role: Role) => void }

type FormValues = RoleFormValues

function sameIds(a: string[], b: string[]) {
  if (a.length !== b.length) return false
  const set = new Set(a)
  return b.every((id) => set.has(id))
}

export function RoleFormModal({ open, onOpenChange, role, onSaved }: Props) {
  return open ? (
    <RoleForm
      key={role?.id ?? 'new'}
      role={role ?? null}
      onClose={() => onOpenChange(false)}
      onDone={(r) => {
        onSaved?.(r)
        onOpenChange(false)
      }}
    />
  ) : null
}

function useRoleSchema() {
  const { t } = useTranslation()
  return z
    .object({
      name: z.string().trim().min(2, t('access.form.nameRequired')).max(60, t('access.form.nameTooLong')),
      description: z.string().trim().min(1, t('access.form.descriptionRequired')).max(500, t('access.form.descriptionTooLong')),
      scope: z.enum(ROLE_SCOPES),
      scopeTargets: z.array(z.string()),
      permissions: z.array(z.string()),
      userIds: z.array(z.string()),
    })
    .refine((v) => (v.scope !== 'project' && v.scope !== 'department') || v.scopeTargets.length > 0, {
      path: ['scopeTargets'],
      message: t('access.form.targetsRequired'),
    })
}

function RoleForm({ role, onDone, onClose }: { role: Role | null; onDone: (r: Role) => void; onClose: () => void }) {
  const { t } = useTranslation()
  const save = useSaveRole()
  const roles = useAccessRoles()
  const { users, departmentById } = useAccessLookups()
  const [tab, setTab] = useState('info')
  const initialUserIds = useMemo(() => role?.assignments.map((a) => a.userId) ?? [], [role])
  const [pendingConfirm, setPendingConfirm] = useState<{ input: RoleInput; warning: UserRoleReassignmentWarning } | null>(null)

  const form = useForm<FormValues>({
    resolver: zodResolver(useRoleSchema()),
    defaultValues: {
      name: role?.name ?? '',
      description: role?.description ?? '',
      scope: role?.scope ?? 'organization',
      scopeTargets: role?.scopeTargets ?? [],
      permissions: role?.permissions ?? [],
      userIds: initialUserIds,
    },
  })
  const [permissions, userIds] = useWatch({ control: form.control, name: ['permissions', 'userIds'] })

  const submit = async (values: FormValues, confirm: boolean) => {
    const usersChanged = !role || !sameIds(values.userIds, initialUserIds)
    const input: RoleInput = {
      name: values.name,
      description: values.description,
      scope: values.scope,
      scopeTargets: values.scopeTargets,
      permissions: values.permissions,
      confirmUserReassignment: confirm,
      ...(role ? (usersChanged ? { userIds: values.userIds } : {}) : { userIds: values.userIds }),
    }
    try {
      const saved = await save.mutateAsync({ id: role?.id, input })
      toast.success(t(role ? 'access.form.updated' : 'access.form.created'))
      setPendingConfirm(null)
      onDone(saved)
    } catch (e) {
      if (!confirm && isApiError(e) && e.status === 409 && e.code === 'USER_ROLE_REASSIGNMENT_REQUIRES_CONFIRMATION') {
        const warning = parseUserRoleReassignmentWarning(e.warning)
        if (warning) {
          setPendingConfirm({ input, warning })
          return
        }
      }
      if (isApiError(e) && e.code === 'ROLE_NAME_TAKEN') {
        setTab('info')
        form.setError('name', { message: t('errors.ROLE_NAME_TAKEN') })
      } else toastError(e)
    }
  }

  const onSubmit = form.handleSubmit(
    (values) => void submit(values, false),
    () => setTab('info'),
  )

  return (
    <>
      <FormDrawer
        open
        onOpenChange={(v) => !v && onClose()}
        size="lg"
        dismissible={!save.isPending}
        bodyClassName="pt-0"
        icon={<ShieldCheck />}
        title={t(role ? 'access.form.editTitle' : 'access.form.createTitle')}
        description={t('access.form.hint')}
        onSubmit={(e) => void onSubmit(e)}
        footer={
          <>
            <p className="mr-auto flex min-w-0 flex-[1_1_12rem] items-start gap-2 text-xs text-muted-foreground">
              <Info className="mt-0.5 size-4 shrink-0 text-brand-blue" />
              <span>
                <span className="font-medium text-foreground">{t('access.form.previewTitle')}: </span>
                {userIds.length ? t('access.form.previewBody', { count: userIds.length, permissions: permissions.length }) : t('access.form.previewEmpty')}
              </span>
            </p>
            <Button type="button" variant="cancel" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? <Loader2 className="animate-spin" /> : null}
              {t(role ? 'access.form.submitEdit' : 'access.form.submitCreate')}
            </Button>
          </>
        }
      >
        <Tabs value={tab} onValueChange={setTab}>
          <div className="sticky top-0 z-10 -mx-5 mb-1 bg-card px-5 pt-4 pb-3">
            <TabsList>
              <TabsTrigger value="info">{t('access.form.tabInfo')}</TabsTrigger>
              <TabsTrigger value="permissions">{t('access.form.tabPermissions', { count: permissions.length })}</TabsTrigger>
              <TabsTrigger value="users">{t('access.form.tabUsers', { count: userIds.length })}</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="info" className="space-y-4">
            <RoleInfoFields form={form} roles={(roles.data ?? []).filter((r) => r.id !== role?.id)} nameLocked={!!role?.isSystem} />
          </TabsContent>
          <TabsContent value="permissions">
            <PermissionPicker value={permissions} onChange={(v) => form.setValue('permissions', v, { shouldDirty: true })} />
          </TabsContent>
          <TabsContent value="users">
            <UserPicker users={users} departmentById={departmentById} value={userIds} onChange={(v) => form.setValue('userIds', v, { shouldDirty: true })} />
          </TabsContent>
        </Tabs>
      </FormDrawer>

      <ConfirmModal
        open={!!pendingConfirm}
        onOpenChange={(v) => !v && !save.isPending && setPendingConfirm(null)}
        title={t('access.users.reassign.title')}
        description={pendingConfirm?.warning.message || t('access.users.reassign.body')}
        confirmLabel={t('access.users.reassign.confirm')}
        loading={save.isPending}
        onConfirm={() => {
          if (!pendingConfirm) return
          void save
            .mutateAsync({ id: role?.id, input: { ...pendingConfirm.input, confirmUserReassignment: true } })
            .then((saved) => {
              toast.success(t(role ? 'access.form.updated' : 'access.form.created'))
              setPendingConfirm(null)
              onDone(saved)
            })
            .catch(toastError)
        }}
      >
        <ul className="max-h-64 space-y-2 overflow-y-auto">
          {pendingConfirm?.warning.affectedUsers.map((u) => (
            <li key={u.id} className="rounded-lg border px-3 py-2 text-sm">
              <p className="font-medium">{u.fullName}</p>
              <p className="text-xs text-muted-foreground">{u.email}</p>
              {u.currentRoleNames.length ? (
                <p className="mt-1 text-xs text-muted-foreground">{t('access.users.reassign.currentRoles', { names: u.currentRoleNames.join(', ') })}</p>
              ) : null}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-warning">{t('access.users.reassign.sessionHint')}</p>
      </ConfirmModal>
    </>
  )
}
